// SIMPLIFIED TODAYS PICKUPS - SINGLE DATA SOURCE
// Uses only reservations collection - no dual data dependencies

import React, { useState, useEffect } from "react";
import { Calendar, User, MapPin, Clock, Phone, CheckCircle, AlertCircle, Building, Shield, Bike, Database, Loader } from "lucide-react";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Simplified TodaysPickups with single data source
const SimplifiedTodaysPickups = ({ 
  currentUser,
  currentCompany, 
  getCompanyContext
}) => {
  // Get company context for data isolation
  const companyId = getCompanyContext ? getCompanyContext() : null;

  // State Management
  const [bikes, setBikes] = useState([]);
  const [bikeDetailsMap, setBikeDetailsMap] = useState(new Map());
  const [todaysPickups, setTodaysPickups] = useState([]);
  const [processingPickup, setProcessingPickup] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Firebase collections - SIMPLIFIED: Only need these two
  const managedBikesCollection = collection(db, 'managedBikes');
  const reservationsCollection = collection(db, `companies/${companyId}/reservations`);

  // Permission check
  const hasPermission = (permission) => {
    if (currentUser?.permissions?.includes(permission)) return true;
    if (currentUser?.role === 'admin') return true;
    return false;
  };

  // Permission guard
  if (!hasPermission('bookings')) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-purple-400" />
            Today's Pickups
            <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded-full">
              Access Denied
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to view today's pickups.</p>
        </div>
      </div>
    );
  }

  // Company context validation
  if (!companyId || !currentCompany) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-purple-400" />
            Today's Pickups
            <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
              Simplified
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-20 w-20 text-yellow-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Company Context Missing</h3>
          <p className="text-gray-500 text-lg">Please ensure you're logged in with a valid company.</p>
        </div>
      </div>
    );
  }

  // Load company bikes from Firebase
  useEffect(() => {
    if (!companyId) return;

    console.log(`Loading bikes for company: ${currentCompany.name} (${companyId})`);
    
    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const loadedBikes = [];
      const detailsMap = new Map();
      
      querySnapshot.forEach((doc) => {
        const bikeData = { id: doc.id, ...doc.data() };
        loadedBikes.push(bikeData);
        
        // Create mapping for quick lookups
        detailsMap.set(bikeData.regNumber, bikeData);
        detailsMap.set(bikeData.id, bikeData);
        detailsMap.set(bikeData.model, bikeData);
      });
      
      setBikes(loadedBikes);
      setBikeDetailsMap(detailsMap);
      
      console.log(`Loaded ${loadedBikes.length} bikes for ${currentCompany.name}`);
    }, (error) => {
      console.error('Error loading bikes:', error);
    });

    return () => unsubscribe();
  }, [companyId]);

  // SIMPLIFIED: Load today's pickups from reservations only
  useEffect(() => {
    if (!companyId) return;

    console.log(`Loading simplified today's pickups for: ${currentCompany.name}`);
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    
    // Query for booked reservations with pickup date today
    const q = query(
      reservationsCollection,
      where('availabilityStatus', '==', 'booked')
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const allReservations = [];
      querySnapshot.forEach((doc) => {
        allReservations.push({ id: doc.id, ...doc.data() });
      });
      
      // Filter for today's pickups and not completed
      const todaysPickupsFiltered = allReservations.filter(reservation => {
        // Skip cancelled or completed reservations
        if (reservation.availabilityStatus === 'cancel' || 
            reservation.rentalStatus === 'completed') {
          return false;
        }
        
        // Check if pickup date is today
        if (reservation.pickupDateTime) {
          const pickupDate = new Date(reservation.pickupDateTime);
          const pickupDateString = pickupDate.toISOString().split('T')[0];
          return pickupDateString === todayString;
        }
        
        return false;
      });
      
      setTodaysPickups(todaysPickupsFiltered);
      setIsInitialLoading(false);
      
      console.log(`Found ${todaysPickupsFiltered.length} pickups for today in ${currentCompany.name}`);
    }, (error) => {
      console.error('Error loading today\'s pickups:', error);
      setIsInitialLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // SIMPLIFIED: Handle pickup confirmation by updating reservation status
  const handleSimplifiedPickupConfirmation = async (reservation) => {
    try {
      setProcessingPickup(reservation.id);
      console.log(`Confirming pickup for reservation: ${reservation.id} (${currentCompany.name})`);
      
      // Update reservation to active status
      await updateDoc(doc(reservationsCollection, reservation.id), {
        rentalStatus: 'active',
        pickedUpAt: new Date().toISOString(),
        pickedUpBy: currentUser?.username || 'Unknown',
        updatedAt: serverTimestamp()
      });
      
      console.log(`Pickup confirmed for reservation ${reservation.id} in ${currentCompany.name}`);
      
    } catch (error) {
      console.error(`Error confirming pickup:`, error);
      alert('Error confirming pickup. Please try again.');
    } finally {
      setProcessingPickup(null);
    }
  };

  // Get bike details for display
  const getBikeDetails = (reservation) => {
    const bikeReference = reservation.motorbikeNumber;
    const details = bikeDetailsMap.get(bikeReference) || 
                   Array.from(bikeDetailsMap.values()).find(bike => 
                     bike.regNumber === bikeReference ||
                     bike.model === bikeReference ||
                     bike.id === bikeReference
                   );
    
    if (details) {
      return {
        model: details.model,
        regNumber: details.regNumber,
        dailyRate: details.dailyRate || 25,
        color: details.color,
        year: details.year,
        isManaged: true
      };
    }
    
    return {
      model: bikeReference || 'Unknown Model',
      regNumber: bikeReference || 'Unknown Registration',
      dailyRate: 25,
      isManaged: false
    };
  };

  // Calculate rental duration
  const calculateRentalDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 1;
  };

  if (isInitialLoading) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-purple-400" />
            Today's Pickups
            <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
              Simplified
            </span>
          </h2>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-purple-500 mr-3" size={32} />
            <div>
              <h3 className="text-white font-medium">Loading Simplified Pickups</h3>
              <p className="text-gray-400 text-sm">Single data source initialization...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-purple-400" />
          Today's Pickups
          <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
            Simplified
          </span>
          <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">
            Single Data Source
          </span>
          {todaysPickups.length > 0 && (
            <span className="ml-3 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
              {todaysPickups.length}
            </span>
          )}
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Simplified pickup management using reservations data only
        </p>
        <div className="mt-2 flex items-center text-blue-400 text-sm">
          <Building size={16} className="mr-1" />
          <span>{currentCompany.name} • {bikes.length} bikes</span>
        </div>
      </div>
      
      <div className="p-6">
        {/* Simplified Architecture Panel */}
        <div className="mb-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Database className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="text-blue-300 font-medium mb-2 flex items-center gap-2">
                Simplified Pickup System: {currentCompany.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-200">Data Source:</span>
                  <div className="text-gray-300">✓ Reservations only</div>
                  <div className="text-gray-400 text-xs">No dual data dependencies</div>
                </div>
                <div>
                  <span className="text-blue-200">Today's Pickups:</span>
                  <div className="text-gray-300">{todaysPickups.length} scheduled</div>
                  <div className="text-gray-400 text-xs">Filtered by pickup date</div>
                </div>
                <div>
                  <span className="text-blue-200">Status Updates:</span>
                  <div className="text-green-300">✓ Direct reservation update</div>
                  <div className="text-gray-400 text-xs">No sync issues</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {todaysPickups.length === 0 ? (
          <div className="bg-gray-700 p-8 rounded-lg border border-gray-600 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <h3 className="text-xl font-medium text-white mb-2">No Pickups Today</h3>
            <p className="text-gray-300">
              No motorcycle pickups scheduled for today in {currentCompany.name}
            </p>
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
              <p className="text-xs text-blue-300 flex items-center justify-center">
                <Shield size={14} className="mr-1" />
                All data sourced from reservations collection for {currentCompany.name}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-400 mb-4">
              Showing {todaysPickups.length} pickup{todaysPickups.length !== 1 ? 's' : ''} scheduled for today
            </div>
            
            {todaysPickups.map((pickup, index) => {
              const bikeDetails = getBikeDetails(pickup);
              
              return (
                <div key={pickup.id || index} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Customer Information */}
                    <div>
                      <h4 className="text-blue-400 font-medium mb-3 flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        Customer Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-400">Name:</span>
                          <p className="text-white font-medium">
                            {pickup.customerName || "Unknown Customer"}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Contact:</span>
                          <div className="flex items-center mt-1">
                            <Phone className="w-3 h-3 mr-1 text-gray-500" />
                            <span className="text-white">
                              {pickup.whatsappNumber || 
                               pickup.localNumber || 
                               "No contact provided"}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-400">Email:</span>
                          <p className="text-white">
                            {pickup.email || "Not provided"}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Reservation ID:</span>
                          <p className="text-white font-medium text-xs">
                            {pickup.id}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bike Information */}
                    <div>
                      <h4 className="text-orange-400 font-medium mb-3 flex items-center gap-2">
                        <Bike className="w-4 h-4" />
                        Bike Details
                        {bikeDetails.isManaged ? (
                          <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                            Managed
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded-full">
                            Basic
                          </span>
                        )}
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-400">Model:</span>
                          <p className="text-white font-medium">{bikeDetails.model}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Registration:</span>
                          <p className="text-white font-medium">{bikeDetails.regNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Daily Rate:</span>
                          <p className="text-white font-medium">
                            ${bikeDetails.dailyRate}
                            {bikeDetails.isManaged ? (
                              <span className="text-green-400 text-xs ml-1">(Managed)</span>
                            ) : (
                              <span className="text-gray-400 text-xs ml-1">(Default)</span>
                            )}
                          </p>
                        </div>
                        {bikeDetails.color && (
                          <div>
                            <span className="text-gray-400">Color:</span>
                            <p className="text-white">{bikeDetails.color}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Rental Information */}
                    <div>
                      <h4 className="text-green-400 font-medium mb-3 flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Rental Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-400">Pickup Date:</span>
                          <p className="text-white font-medium">
                            {pickup.pickupDateTime ? new Date(pickup.pickupDateTime).toLocaleDateString() : "Today"}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Return Date:</span>
                          <p className="text-white">
                            {pickup.dropoffDateTime ? new Date(pickup.dropoffDateTime).toLocaleDateString() : "Not specified"}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Duration:</span>
                          <p className="text-white">
                            {pickup.pickupDateTime && pickup.dropoffDateTime 
                              ? `${calculateRentalDuration(pickup.pickupDateTime, pickup.dropoffDateTime)} days`
                              : "Unknown"}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Rental Amount:</span>
                          <p className="text-white font-medium">${pickup.rentalAmount || "0"}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Status:</span>
                          <p className="text-white">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              pickup.rentalStatus === 'active' ? 'bg-green-900 text-green-400' :
                              pickup.rentalStatus === 'completed' ? 'bg-gray-900 text-gray-400' :
                              'bg-blue-900 text-blue-400'
                            }`}>
                              {pickup.rentalStatus || 'Reserved'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pickup Location */}
                  {pickup.pickupLocation && (
                    <div className="mt-4 pt-4 border-t border-gray-600">
                      <h5 className="text-purple-400 text-sm font-medium mb-2 flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        Pickup Location
                      </h5>
                      <p className="text-white text-sm">
                        {pickup.pickupLocation === 'SLBR' 
                          ? `${currentCompany.name} Office` 
                          : pickup.customPickupLocation || pickup.pickupLocation || 'Custom Location'}
                      </p>
                    </div>
                  )}

                  {/* Simplified Data Source Indicator */}
                  <div className="mt-4 p-2 bg-blue-900/20 border border-blue-700 rounded text-center">
                    <div className="text-xs text-blue-300 flex items-center justify-center">
                      <Database size={12} className="mr-1" />
                      Simplified System: {currentCompany.name} • Reservations Data Only
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-4 flex justify-end">
                    {pickup.rentalStatus !== 'active' && pickup.rentalStatus !== 'completed' ? (
                      <button
                        onClick={() => handleSimplifiedPickupConfirmation(pickup)}
                        disabled={processingPickup === pickup.id}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {processingPickup === pickup.id ? (
                          <>
                            <Loader className="animate-spin" size={16} />
                            <span>Confirming...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle size={16} />
                            <span>Confirm Pickup</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center">
                        <CheckCircle size={14} className="mr-1" />
                        {pickup.rentalStatus === 'active' ? 'Active' : 'Completed'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplifiedTodaysPickups;